import React, { forwardRef } from 'react';

import type { ButtonBaseProps } from '@mui/material';
import { alpha, ButtonBase } from '@mui/material';
import clsx from 'clsx';
import { getTypographyForSx } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

export interface SelectButtonProps extends ButtonBaseProps {
  buttonColor?: string;
  fullWidth?: boolean;
  labelClass?: string;
  selected?: boolean;
  variant?: 'default' | 'outlined' | 'rightToolbar';
}

const useStyles = makeStyles<Pick<SelectButtonProps, 'buttonColor' | 'disabled' | 'selected'>>()(
  (theme, { buttonColor = theme.palette.primary.main, disabled, selected }) => ({
    fullWidth: {
      width: '100%',
    },
    /* Styles applied to the `label` wrapper element. */
    label: {
      alignItems: 'inherit',
      display: 'inherit',
      justifyContent: 'inherit',
      whiteSpace: 'nowrap',
      width: '100%', // Ensure the correct width for iOS Safari
    },
    outlined: {
      // Bordered buttons sit on the grey page background, so they need to be filled.
      backgroundColor: theme.palette.background.paper,
      border: `1px ${theme.appColors.rule} solid`,
    },
    rightToolbar: {
      ...getTypographyForSx(theme.typography.button),
      backgroundColor: selected ? alpha(buttonColor, 0.05) : undefined,
      borderRadius: 8,
      boxSizing: 'border-box',
      color: disabled
        ? theme.palette.action.disabled
        : selected
          ? theme.appColors.primary
          : theme.appColors.iconsType,
      padding: 8,
      textDecoration: 'none',
    },
    root: (() => {
      const selectedColor = alpha(buttonColor, 0.15);
      return {
        ...getTypographyForSx(theme.typography.button),
        '&:active': {
          color: disabled ? theme.palette.action.disabled : buttonColor,
        },
        // .Mui-focusVisible handles focus styling when used inside the Menubar component
        '&:hover, &.Mui-focusVisible': {
          backgroundColor: disabled
            ? 'transparent'
            : selected
              ? selectedColor
              : alpha(buttonColor, 0.06),
        },
        backgroundColor: disabled ? 'transparent' : selected ? selectedColor : undefined,
        borderColor: disabled ? theme.palette.action.disabled : selected ? buttonColor : undefined,
        borderRadius: 8,
        boxSizing: 'border-box',
        color: disabled
          ? theme.palette.action.disabled
          : selected
            ? buttonColor
            : theme.appColors.iconsType,
        padding: theme.spacing(0.5),
        textTransform: 'none',
      };
    })(),
  })
);

export const SelectButton = forwardRef(function SelectButton(
  props: SelectButtonProps,
  ref: React.Ref<HTMLButtonElement>
): JSX.Element {
  const {
    buttonColor,
    children,
    className,
    disabled,
    fullWidth,
    labelClass,
    selected = false,
    variant = 'default',
    ...rest
  } = props;
  const { classes } = useStyles({ buttonColor, disabled, selected });

  return (
    <ButtonBase
      ref={ref}
      className={clsx(
        {
          [classes.root]: variant === 'default' || variant === 'outlined',
          [classes.outlined]: variant === 'outlined',
          [classes.rightToolbar]: variant === 'rightToolbar',
          [classes.fullWidth]: fullWidth,
        },
        className
      )}
      disabled={disabled}
      {...rest}
    >
      <span className={clsx(classes.label, labelClass)}>{children}</span>
    </ButtonBase>
  );
});
